// TODO(SPRK-3871): Extract shared crypto utils into @newcore/shared-crypto workspace package
/**
 * Threshold RSA cryptographic utilities for split SAML second signing.
 *
 * Uses native ES2020 BigInt -- zero external dependencies.
 *
 * Note: computeSigma2 lives in the browser extension (browser-extension/src/threshold-crypto.ts).
 * This module only contains the combine function used by the frontend.
 */

/**
 * Convert Uint8Array to BigInt (big-endian unsigned).
 */
export function bytesToBigInt(bytes: Uint8Array): bigint {
  if (bytes.length === 0) {
    return 0n;
  }

  let hex = '0x';

  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }

  return BigInt(hex);
}

/**
 * Convert BigInt to Uint8Array of exactly `length` bytes (big-endian, left-padded).
 */
export function bigIntToBytes(value: bigint, length: number): Uint8Array {
  const hex = value.toString(16).padStart(length * 2, '0');
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }

  return bytes;
}

/**
 * Combine sigma1 and sigma2 into a valid RSA signature.
 * combined = (sigma1 * sigma2) mod n
 */
export function combineSignatures(
  sigma1Bytes: Uint8Array,
  sigma2Bytes: Uint8Array,
  modulusHex: string
): Uint8Array {
  const sigma1 = bytesToBigInt(sigma1Bytes);
  const sigma2 = bytesToBigInt(sigma2Bytes);
  const modulus = BigInt(modulusHex);

  const combined = (sigma1 * sigma2) % modulus;
  const keyByteLength = Math.ceil(modulus.toString(16).length / 2);

  return bigIntToBytes(combined, keyByteLength);
}

function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let square = base % modulus;
  let remaining = exponent;

  while (remaining > 0n) {
    const bitIsSet = (remaining & 1n) === 1n;

    if (bitIsSet) {
      result = (result * square) % modulus;
    }

    square = (square * square) % modulus;
    remaining >>= 1n;
  }

  return result;
}

// n and e come from the device: catches a mismatched share, not a forged one.
export function signatureCompletes(
  signatureBytes: Uint8Array,
  paddedDigestHex: string,
  key: { modulus: string; publicExponent: number }
): boolean {
  const signature = bytesToBigInt(signatureBytes);
  // modulus arrives 0x-prefixed (keyShare.n); the digest arrives bare (Go hex).
  const modulus = BigInt(key.modulus);
  const publicExponent = BigInt(key.publicExponent);
  const paddedDigest = BigInt(`0x${paddedDigestHex}`);

  return modPow(signature, publicExponent, modulus) === paddedDigest;
}
